import {
  dimensionsAffichables,
  formatDimensions,
  type Bilingual,
  type Dimensions,
  type EcartImportOeuvres,
  type LigneImportMiseAJour,
  type RapportImportMiseAJour,
} from '@artosera/shared';
import { Button, Dialog, formatPrice, Select, Tabs } from '@artosera/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import {
  oeuvresApi,
  rapportDesOeuvres,
  useImporterOeuvres,
  useImportMiseAJour,
  type FiltresOeuvres,
  type ReponseImport,
} from '../../api/oeuvres.js';
import { enregistrerFichier } from '../../api/client.js';
import { cleMetier, dansLaPorteeCourante } from '../../api/cles.js';
import { RapportRepriseVue } from './RapportRepriseVue.js';
import { useShellContext } from '../shell/context.js';
import { galerieALaction } from '../../auth/sujet.js';
import styles from './ImportExportDialog.module.css';

export interface ImportExportDialogProps {
  galerieSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Les filtres de la liste affichée : l'export rend ce que la galerie voit
   * (recette import/export, défaut 19). Pagination et tri n'en font pas partie.
   */
  filtres?: FiltresOeuvres;
}

/** Délai entre deux relectures d'un import mis en file. */
export const RELECTURE_IMPORT_MS = 3000;

/** États d'un import en travail qui appellent une nouvelle relecture. */
const EN_ATTENTE = new Set<ReponseImport['etat']>(['enfile', 'en_cours']);

/** Les filtres de la liste qui ont un sens pour l'export. */
function filtresExport(filtres: FiltresOeuvres | undefined): FiltresOeuvres {
  if (filtres === undefined) return {};
  const utiles: FiltresOeuvres = { ...filtres };
  delete utiles.vignettes;
  delete utiles.page;
  delete utiles.parPage;
  delete utiles.tri;
  return utiles;
}

function filtresActifs(filtres: FiltresOeuvres): boolean {
  return Object.entries(filtres).some(([cle, valeur]) =>
    cle === 'inclureArchivees'
      ? valeur === true
      : Array.isArray(valeur)
        ? valeur.length > 0
        : valeur !== undefined && valeur !== '',
  );
}

/**
 * Import et export (mission S3-a, point 6, `docs/dev/api.md` § 13, 12.9) :
 * rapport ligne par ligne (lignes refusées, avertissements, colonnes
 * ignorées), SUIVI du travail au-delà de 200 lignes jusqu'à son rapport ou son
 * échec (recette import/export, défaut 7), export CSV/XLSX des œuvres que la
 * liste montre.
 */
export function ImportExportDialog({
  galerieSlug,
  open,
  onOpenChange,
  filtres,
}: ImportExportDialogProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun', 'ventes']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const client = useQueryClient();

  const coquille = useShellContext();
  const prixPublicAutorise = coquille?.galerie
    ? galerieALaction(coquille.galerie, 'artworks:bulk-price')
    : false;
  const [mode, setMode] = useState<'creation' | 'miseAJour'>('creation');
  const [rapportMiseAJour, setRapportMiseAJour] = useState<RapportImportMiseAJour | null>(null);
  const fichierMiseAJour = useRef<File | null>(null);
  const cleConfirmation = useRef<string | null>(null);
  const miseAJour = useImportMiseAJour(galerieSlug);

  const fichierRef = useRef<HTMLInputElement>(null);
  const imagesRef = useRef<HTMLInputElement>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [reponse, setReponse] = useState<ReponseImport | null>(null);
  /** L'import en travail que l'on relit ; `null` hors suivi. */
  const [suivi, setSuivi] = useState<string | null>(null);
  const importer = useImporterOeuvres(galerieSlug);

  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');
  const [exportEnCours, setExportEnCours] = useState(false);
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const filtresDeLExport = filtresExport(filtres);

  useEffect(() => {
    if (suivi === null || !open) return;
    let annule = false;
    let minuterie: ReturnType<typeof setTimeout> | undefined;
    async function relire(): Promise<void> {
      try {
        const resultat = await dansLaPorteeCourante((appel) =>
          oeuvresApi.suivreImport(galerieSlug, suivi!, appel),
        );
        if (annule) return;
        if (EN_ATTENTE.has(resultat.etat)) {
          setReponse(resultat);
          minuterie = setTimeout(() => void relire(), RELECTURE_IMPORT_MS);
          return;
        }
        setSuivi(null);
        setReponse(resultat);
        if (resultat.etat === 'termine') {
          // Les œuvres sont créées par le travail, après la réponse 202.
          void client.invalidateQueries({ queryKey: cleMetier(galerieSlug, 'oeuvres') });
          void client.invalidateQueries({ queryKey: cleMetier(galerieSlug, 'artistes') });
        } else {
          setErreur(resultat.message ?? t('regie:oeuvres.importExport.echoue'));
        }
      } catch (error) {
        if (annule) return;
        setSuivi(null);
        setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
      }
    }
    void relire();
    return () => {
      annule = true;
      if (minuterie !== undefined) clearTimeout(minuterie);
    };
  }, [suivi, open, galerieSlug, client, t]);

  async function soumettreImport(): Promise<void> {
    setErreur(null);
    setReponse(null);
    setSuivi(null);
    const fichier = fichierRef.current?.files?.[0];
    if (!fichier) return;
    const images = imagesRef.current?.files?.[0] ?? null;
    try {
      const resultat = await importer.mutateAsync({ fichier, images });
      setReponse(resultat);
      if (EN_ATTENTE.has(resultat.etat) && resultat.importId) setSuivi(resultat.importId);
    } catch (error) {
      if (estApiError(error)) setErreur(error.message);
      else if (estErreurReseau(error)) setErreur(t('commun:erreurReseau'));
      else setErreur(t('commun:erreurReseau'));
    }
  }

  async function apercuMiseAJour(fichier: File): Promise<void> {
    setErreur(null);
    setRapportMiseAJour(null);
    cleConfirmation.current = null;
    try {
      const rapport = await miseAJour.mutateAsync({ fichier, demande: { essai: true } });
      fichierMiseAJour.current = fichier;
      setRapportMiseAJour(rapport);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  async function confirmerMiseAJour(): Promise<void> {
    const fichier = fichierMiseAJour.current;
    if (!rapportMiseAJour?.essai || !fichier) return;
    cleConfirmation.current ??= crypto.randomUUID();
    setErreur(null);
    try {
      const rapport = await miseAJour.mutateAsync({
        fichier,
        demande: {
          essai: false,
          empreinteApercu: rapportMiseAJour.empreinte,
          cleOperation: cleConfirmation.current,
        },
      });
      setRapportMiseAJour(rapport);
    } catch (error) {
      if (
        estApiError(error) &&
        error.status === 409 &&
        (error.details as { raison?: string } | null)?.raison === 'apercu_perime'
      ) {
        await apercuMiseAJour(fichier);
        setErreur(t('regie:oeuvres.importExport.miseAJour.perime'));
      } else setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  function presenterValeur(valeur: unknown, champ: string, devise: string): string {
    if (valeur === null || valeur === undefined || valeur === '') return '—';
    if (champ === 'prixPublicCentimes' || champ === 'prixArtisteCentimes')
      return formatPrice(valeur as number, langue, devise);
    if (champ === 'dimensions')
      return formatDimensions(dimensionsAffichables(valeur as Dimensions), langue);
    if (champ === 'regimeTva') return t(`ventes:regimes.${String(valeur)}`);
    if (typeof valeur === 'boolean')
      return t(
        valeur
          ? 'regie:oeuvres.importExport.miseAJour.oui'
          : 'regie:oeuvres.importExport.miseAJour.non',
      );
    if (Array.isArray(valeur)) return valeur.join(', ') || '—';
    return String(valeur);
  }

  function presenterEcart(ecart: EcartImportOeuvres, ligne: LigneImportMiseAJour): JSX.Element[] {
    const libelle = t(`regie:oeuvres.importExport.miseAJour.champs.${ecart.champ}`);
    if (ecart.champ === 'technique' || ecart.champ === 'description') {
      const avant = ecart.avant as Bilingual | null;
      const apres = ecart.apres as Bilingual | null;
      return (['fr', 'en'] as const)
        .filter((langueTexte) => avant?.[langueTexte] !== apres?.[langueTexte])
        .map((langueTexte) => (
          <li key={`${ecart.champ}-${langueTexte}`}>
            {libelle} ({langueTexte.toUpperCase()}) : {avant?.[langueTexte] || '—'} →{' '}
            {apres?.[langueTexte] || '—'}
          </li>
        ));
    }
    const estReference = ecart.champ === 'artisteId' || ecart.champ === 'localisationId';
    const avant = estReference ? ecart.avantNom : ecart.avant;
    const apres = estReference ? ecart.apresNom : ecart.apres;
    const deviseAvant = ligne.devise ?? 'EUR';
    const deviseModifiee = ligne.champs?.find((champ) => champ.champ === 'devise');
    const deviseApres = deviseModifiee ? String(deviseModifiee.apres) : deviseAvant;
    return [
      <li key={ecart.champ}>
        {libelle} : {presenterValeur(avant, ecart.champ, deviseAvant)} →{' '}
        {presenterValeur(apres, ecart.champ, deviseApres)}
      </li>,
    ];
  }

  async function telecharger(): Promise<void> {
    setErreurExport(null);
    setExportEnCours(true);
    try {
      const fichier = await dansLaPorteeCourante((appel) =>
        oeuvresApi.exporter(galerieSlug, format, filtresDeLExport, appel),
      );
      enregistrerFichier(fichier, `oeuvres.${format}`);
    } catch (error) {
      if (estApiError(error)) setErreurExport(error.message);
      else setErreurExport(t('commun:erreurReseau'));
    } finally {
      setExportEnCours(false);
    }
  }

  const rapport = reponse?.rapport ?? null;

  return (
    <Dialog
      open={open}
      onOpenChange={(ouvert) => {
        onOpenChange(ouvert);
        if (!ouvert) {
          setReponse(null);
          setErreur(null);
          setSuivi(null);
          setRapportMiseAJour(null);
          fichierMiseAJour.current = null;
          cleConfirmation.current = null;
        }
      }}
      title={t('regie:oeuvres.importExport.titre')}
      closeLabel={t('commun:actions.fermer')}
    >
      <Tabs
        tabs={[
          {
            value: 'import',
            label: t('regie:oeuvres.importExport.onglet_import'),
            content: (
              <div className={styles.onglet}>
                <Select
                  aria-label={t('regie:oeuvres.importExport.miseAJour.mode')}
                  value={mode}
                  disabled={importer.isPending || miseAJour.isPending || suivi !== null}
                  onValueChange={(valeur) => {
                    setMode(valeur as 'creation' | 'miseAJour');
                    setReponse(null);
                    setErreur(null);
                    setRapportMiseAJour(null);
                    cleConfirmation.current = null;
                  }}
                  options={[
                    { value: 'creation', label: t('regie:oeuvres.importExport.miseAJour.creer') },
                    {
                      value: 'miseAJour',
                      label: t('regie:oeuvres.importExport.miseAJour.mettreAJour'),
                    },
                  ]}
                />
                {mode === 'miseAJour' ? (
                  <>
                    <p className={styles.info}>
                      {t('regie:oeuvres.importExport.miseAJour.consigne')}
                    </p>
                    {!prixPublicAutorise ? (
                      <p className={styles.info}>
                        {t('regie:oeuvres.importExport.miseAJour.prixDirection')}
                      </p>
                    ) : null}
                  </>
                ) : null}
                <label className={styles.champFichier}>
                  {t('regie:oeuvres.importExport.fichierChamp')}
                  <input
                    ref={fichierRef}
                    type="file"
                    accept=".csv,.xlsx"
                    disabled={importer.isPending || miseAJour.isPending}
                    onChange={() => {
                      setRapportMiseAJour(null);
                      cleConfirmation.current = null;
                    }}
                  />
                </label>
                {mode === 'creation' ? (
                  <>
                    <label className={styles.champFichier}>
                      {t('regie:oeuvres.importExport.imagesChamp')}
                      <input ref={imagesRef} type="file" accept=".zip" />
                    </label>
                    <Button
                      variant="secondary"
                      onClick={() => void soumettreImport()}
                      loading={importer.isPending || suivi !== null}
                    >
                      {t('regie:oeuvres.importExport.importerAction')}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const fichier = fichierRef.current?.files?.[0];
                      if (fichier) void apercuMiseAJour(fichier);
                    }}
                    loading={miseAJour.isPending}
                  >
                    {t('regie:oeuvres.importExport.miseAJour.apercu')}
                  </Button>
                )}
                {mode === 'miseAJour' && rapportMiseAJour ? (
                  <section className={styles.rapport} aria-live="polite">
                    <h3>
                      {t(
                        rapportMiseAJour.essai
                          ? 'regie:oeuvres.importExport.miseAJour.titreApercu'
                          : 'regie:oeuvres.importExport.miseAJour.applique',
                      )}
                    </h3>
                    <p>
                      {t('regie:oeuvres.importExport.miseAJour.compteurs', {
                        ...rapportMiseAJour.compteurs,
                      })}
                    </p>
                    {rapportMiseAJour.colonnesIgnorees.length > 0 ? (
                      <p>
                        {t('regie:oeuvres.importExport.miseAJour.ignorees', {
                          liste: rapportMiseAJour.colonnesIgnorees.join(', '),
                        })}
                      </p>
                    ) : null}
                    {rapportMiseAJour.colonnesInconnues.length > 0 ? (
                      <p>
                        {t('regie:oeuvres.importExport.miseAJour.inconnues', {
                          liste: rapportMiseAJour.colonnesInconnues.join(', '),
                        })}
                      </p>
                    ) : null}
                    <ul>
                      {rapportMiseAJour.lignes
                        .filter((ligne) => ligne.resultat !== 'inchangee')
                        .map((ligne) => (
                          <li key={ligne.ligne}>
                            {t('regie:oeuvres.importExport.rapportLigne', {
                              ligne: ligne.ligne,
                              titre: ligne.titre ?? ligne.numeroInventaire,
                            })}
                            {ligne.motif ? <p>{ligne.motif}</p> : null}
                            {ligne.champs ? (
                              <ul>
                                {ligne.champs.flatMap((ecart) => presenterEcart(ecart, ligne))}
                              </ul>
                            ) : null}
                          </li>
                        ))}
                    </ul>
                    {rapportMiseAJour.essai && rapportMiseAJour.compteurs.modifiees > 0 ? (
                      <Button
                        onClick={() => void confirmerMiseAJour()}
                        loading={miseAJour.isPending}
                      >
                        {t('regie:oeuvres.importExport.miseAJour.confirmer', {
                          count: rapportMiseAJour.compteurs.modifiees,
                        })}
                      </Button>
                    ) : null}
                  </section>
                ) : null}

                {erreur ? (
                  <p className={styles.erreur} role="alert">
                    {erreur}
                  </p>
                ) : null}

                {suivi !== null ? (
                  <p className={styles.info} role="status">
                    {t('regie:oeuvres.importExport.suiviTravail')}{' '}
                    {t('regie:oeuvres.importExport.suiviEnCours')}
                  </p>
                ) : null}

                {reponse?.finalisation === 'incomplete' && reponse.message ? (
                  <p className={styles.info}>{reponse.message}</p>
                ) : null}

                {rapport ? <RapportRepriseVue rapport={rapportDesOeuvres(rapport)} /> : null}
              </div>
            ),
          },
          {
            value: 'export',
            label: t('regie:oeuvres.importExport.onglet_export'),
            content: (
              <div className={styles.onglet}>
                <label className={styles.champFichier}>
                  {t('regie:oeuvres.importExport.formatChamp')}
                  <Select
                    aria-label={t('regie:oeuvres.importExport.formatChamp')}
                    value={format}
                    onValueChange={(valeur) => setFormat(valeur as 'csv' | 'xlsx')}
                    options={[
                      { value: 'csv', label: 'CSV' },
                      { value: 'xlsx', label: 'XLSX (Excel)' },
                    ]}
                  />
                </label>
                {filtresActifs(filtresDeLExport) ? (
                  <p className={styles.info}>{t('regie:oeuvres.importExport.exportFiltre')}</p>
                ) : null}
                <Button onClick={() => void telecharger()} loading={exportEnCours}>
                  {t('regie:oeuvres.importExport.exporterAction')}
                </Button>
                {erreurExport ? (
                  <p className={styles.erreur} role="alert">
                    {erreurExport}
                  </p>
                ) : null}
              </div>
            ),
          },
        ]}
      />
    </Dialog>
  );
}
